import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { PrintOrderService } from '@/services/database/PrintOrderService';
import { ActivationPackAssetStorageService } from '@/services/activation/ActivationPackAssetStorageService';
import { generateLookbookDesignPdf } from '@/services/activation/lookbookPdf';
import { generatePosterDesignPdf } from '@/services/activation/posterPdf';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { LookbookDesign } from '@/types/activation/lookbook';
import { PosterDesign } from '@/types/activation/poster';
import {
  PrintOrder,
  PrintOrderItem,
  SendPrintOrderParams,
  UpdatePrintOrderParams,
} from '@/types/activation/print-order';

export const usePrintOrder = (activationPackId: string) => {
  return useQuery({
    queryKey: ['print-order', activationPackId],
    queryFn: () => PrintOrderService.getOrCreate(activationPackId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!activationPackId,
  });
};

export const useUpdatePrintOrder = (
  activationPackId: string,
  assetInputRevision?: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      updates,
      expectedUpdatedAt,
    }: {
      orderId: string;
      updates: UpdatePrintOrderParams;
      expectedUpdatedAt: string;
    }) =>
      PrintOrderService.updateOrder(
        orderId,
        updates,
        assetInputRevision,
        expectedUpdatedAt,
      ),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData<PrintOrder | null>(['print-order', activationPackId], data);
      }
    },
    onError: async (error: Error) => {
      await queryClient.invalidateQueries({
        queryKey: ['print-order', activationPackId],
        refetchType: 'active',
      });
      toast.error(error.message || 'Failed to update print order');
    },
  });
};

export const useSendPrintOrder = (
  activationPackId: string,
  genieId?: string | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, params }: { orderId: string; params: SendPrintOrderParams }) =>
      PrintOrderService.sendOrder(orderId, params),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData<PrintOrder | null>(['print-order', activationPackId], data);
      }
      // Activation funnel — artwork actually went to a printer. This is the
      // only `pack_shared` emit that means physical collateral exists.
      ActivationFunnelService.packShared(genieId ?? null, 'print', {
        item_count: data?.items?.length ?? null,
      });
      toast.success('Print order sent');
    },
    onError: async (error: Error) => {
      // Delivery-unknown failures intentionally leave the server order locked
      // as `sending`. Keep the mutation pending until the authoritative state
      // is back in cache so the form cannot briefly invite a duplicate send.
      await queryClient.invalidateQueries({
        queryKey: ['print-order', activationPackId],
        refetchType: 'active',
      });
      toast.error(error.message || 'Failed to send print order');
    },
  });
};

export const useStartNewPrintOrder = (activationPackId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => PrintOrderService.getOrCreate(activationPackId, true),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData<PrintOrder | null>(['print-order', activationPackId], data);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start a new print order');
    },
  });
};

type PrintableDesign = LookbookDesign | PosterDesign;
type PrintableDesignKind = 'lookbook' | 'poster';

// One merge-into-order at a time per pack. Each design add reads the full
// items array and writes it back; two in flight (say a lookbook and a poster
// added back-to-back) would interleave and the later write would silently
// drop the earlier item.
const mergeQueues = new Map<string, Promise<unknown>>();

const enqueueMerge = <T,>(packId: string, task: () => Promise<T>): Promise<T> => {
  const prev = mergeQueues.get(packId) ?? Promise.resolve();
  const next = prev.then(task, task);
  mergeQueues.set(packId, next.catch(() => undefined));
  return next;
};

/**
 * Turns a hand-picked set of lookbook/poster designs into print order line
 * items: rasterizes each selected design to its own print-ready PDF, uploads
 * it to storage, then merges it into the pack's print order (bumping
 * quantity in place if that design is already in the order).
 * Resolves `true` only when the designs actually landed in the order.
 */
export const useAddDesignsToPrintOrder = (
  pack: ActivationPackWithTarget,
  kind: PrintableDesignKind,
) => {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);

  const addDesigns = useCallback(
    async (designs: PrintableDesign[], quantity: number): Promise<boolean> => {
      if (designs.length === 0) return false;
      setIsAdding(true);
      const sourceAssetInputRevision = pack.asset_input_revision;
      try {
        if (!sourceAssetInputRevision) {
          throw new Error('Reload the activation pack before adding print designs');
        }
        // Rasterize + upload first — the slow part — so the read-merge-write
        // against the order below holds the queue for milliseconds, not the
        // seconds html2canvas needs.
        const uploads: { design: PrintableDesign; url: string }[] = [];
        for (const design of designs) {
          const blob =
            kind === 'lookbook'
              ? await generateLookbookDesignPdf(pack, design as LookbookDesign)
              : await generatePosterDesignPdf(pack, design as PosterDesign);
          const uploaded = await ActivationPackAssetStorageService.uploadDesignPdf(
            pack.id,
            kind,
            design.id,
            blob,
          );
          uploads.push({ design, url: uploaded.url });
        }

        const updated = await enqueueMerge(pack.id, async () => {
          const order = await PrintOrderService.getOrCreate(pack.id);
          if (!order) throw new Error('Could not open your print order');
          if (order.asset_input_revision !== sourceAssetInputRevision) {
            throw new Error(
              'This activation pack changed while the design was rendering. Reload and add it again.',
            );
          }

          const items = [...order.items];
          for (const { design, url } of uploads) {
            const itemId = `design:${kind}:${design.id}`;
            const existingIndex = items.findIndex((item) => item.id === itemId);
            const item: PrintOrderItem = {
              id: itemId,
              label: design.name,
              url,
              quantity: existingIndex >= 0 ? items[existingIndex].quantity + quantity : quantity,
              notes: design.meta,
              assetType: kind === 'lookbook' ? 'lookbook_pdf' : 'poster_pack_pdf',
            };

            if (existingIndex >= 0) items[existingIndex] = item;
            else items.push(item);
          }

          try {
            return await PrintOrderService.updateOrder(
              order.id,
              { items },
              sourceAssetInputRevision,
              order.updated_at,
            );
          } catch (updateError) {
            // A write can commit even when its HTTP response is lost. Recover
            // only when the authoritative draft contains this attempt's exact
            // immutable URLs and quantities; otherwise a retry must remain a
            // failure so it cannot silently double the physical print count.
            try {
              const recovered = await PrintOrderService.getOrCreate(pack.id);
              const attemptedItemIds = new Set(
                uploads.map(({ design }) => `design:${kind}:${design.id}`),
              );
              const attemptedItems = items.filter((item) =>
                attemptedItemIds.has(item.id)
              );
              const recoveredExactItems = attemptedItems.every((expected) => {
                  const actual = recovered?.items.find(
                    (item) => item.id === expected.id,
                  );
                  return actual?.id === expected.id &&
                    actual.label === expected.label &&
                    actual.url === expected.url &&
                    actual.quantity === expected.quantity &&
                    (actual.notes || null) === (expected.notes || null) &&
                    (actual.assetType || null) === (expected.assetType || null);
                });
              if (
                recovered?.asset_input_revision === sourceAssetInputRevision &&
                recoveredExactItems
              ) {
                return recovered;
              }
            } catch {
              // Keep the original update error when recovery is unavailable.
            }
            throw updateError;
          }
        });

        if (updated) {
          queryClient.setQueryData<PrintOrder | null>(['print-order', pack.id], updated);
        }
        toast.success(`Added ${designs.length} ${designs.length === 1 ? 'design' : 'designs'} to your print order`);
        return true;
      } catch (error) {
        console.error(`Failed to add ${kind} designs to print order:`, error);
        toast.error("Couldn't add those to your print order");
        return false;
      } finally {
        setIsAdding(false);
      }
    },
    [pack, kind, queryClient],
  );

  return { addDesigns, isAdding };
};
